import { useCallback, useSyncExternalStore } from 'react';
import { breakpoints } from '../tokens/breakpoints';

/**
 * useMediaQuery — subscribe to a CSS media query and re-render on change.
 *
 * This is how the v2 shell does responsive layout: components style via inline
 * `style={}` token objects (no CSS classes, so no @media rules), so the
 * breakpoint has to be observed in JS and branched in the style object.
 *
 * Backed by useSyncExternalStore for a tear-free read that stays correct across
 * concurrent renders. getServerSnapshot returns false (desktop-first) so a
 * non-browser render never touches window.matchMedia.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [query],
  );

  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/**
 * useIsCompact — true below the `lg` breakpoint (1024px), where the shell
 * collapses the persistent sidebar into an off-canvas drawer. Single source of
 * the shell's "phone/tablet vs desktop" decision.
 */
export function useIsCompact(): boolean {
  return useMediaQuery(`(max-width: ${breakpoints.lg - 1}px)`);
}
